import type { LiveUpdatesProvider } from 'api/liveUpdates.types'
import type { OrderingProvider } from 'models/Merchant'

// Olo's provider key as it arrives on the LiveUpdates stack and on an
// order-status webhook receipt row. Typed against the merchant's
// `ordering_provider` union so the RecoveryAI gate
// (`merchant.ordering_provider === OLO_PROVIDER_KEY`) and the LiveUpdates
// wire checks share one literal.
export const OLO_PROVIDER_KEY: OrderingProvider = 'olo'

// Two gates ask "is this Olo?" at different scopes: the scene checks the
// configuration's ORDERING provider (does this merchant get the webhook UI at
// all), while StatusCard checks whichever provider backs THIS status's signal
// (does this particular card get the connection block). The scopes are meant
// to differ; the definition of "is Olo" is not. Olo is only ever an ordering
// provider today, so the two can't disagree in practice — but nothing
// enforces that, and when they were two separate `=== 'olo'` literals a change
// to one was invisible to the other.
export const isOloProvider = (
  provider: LiveUpdatesProvider | null | undefined
): boolean => provider?.key === OLO_PROVIDER_KEY

// Three states, driven by two facts: is a shared secret saved, and has Olo
// ever sent us anything. 'waiting' exists to separate "just configured, the
// first webhook takes 1-2 hours to land" from a working connection — those
// merchants are mid-setup and need reassurance, not an error. Recency is
// deliberately NOT part of the state (see `isOloWebhookStale`).
export type OloWebhookConnectionState =
  | 'not_connected'
  | 'waiting'
  | 'connected'

// Everything the connection surfaces need, as one unit. Their only control is
// the setup/manage link, so a state without a handler is a live-looking button
// that does nothing — passing them as separate optionals let a caller do
// exactly that. Bundled, omitting the handler is a compile error.
//
// `connectionState` is deliberately required: "the queries haven't settled" is
// expressed by leaving the whole object undefined (no panel), not by a present
// object holding an undefined state.
export type OloWebhookPanel = {
  connectionState: OloWebhookConnectionState
  /** ISO timestamp of the most recent Olo webhook — names the age in the
   * connected banner. Null when there has never been one. */
  lastReceivedAt?: string | null
  /** Connected, but silent for over a day: swaps the banner's CTA to
   * "Reconnect Olo webhooks" without changing the state. */
  needsReconnect: boolean
  onManage: () => void
}

export const STALE_AFTER_MS = 24 * 60 * 60 * 1000

export function deriveOloWebhookConnectionState(
  configured: boolean,
  lastReceivedAt: string | null
): OloWebhookConnectionState {
  if (!configured) return 'not_connected'
  if (!lastReceivedAt) return 'waiting'
  return Number.isFinite(new Date(lastReceivedAt).getTime())
    ? 'connected'
    : 'waiting'
}

// Staleness is a modifier on 'connected', not a state of its own — it only
// swaps the banner's CTA. A missing/unparseable timestamp is 'waiting', which
// has its own copy, so it must not also read as stale here.
export function isOloWebhookStale(
  lastReceivedAt: string | null,
  now: number
): boolean {
  if (!lastReceivedAt) return false

  const receivedAtMs = new Date(lastReceivedAt).getTime()
  if (!Number.isFinite(receivedAtMs)) return false

  return now - receivedAtMs >= STALE_AFTER_MS
}

// The most recent last_received_at across every Olo receipt row (a
// configuration can span many locations; the connection status is
// merchant-wide, not per-location). Malformed/unparseable timestamps are
// ignored rather than winning a comparison against a valid one.
export function latestOloReceivedAt(
  receipts: { provider: string; lastReceivedAt: string | null }[]
): string | null {
  // The running max carries its parsed epoch alongside the ISO string so the
  // comparison doesn't re-parse the incumbent on every row.
  return (
    receipts.reduce<{ iso: string; ms: number } | null>((latest, receipt) => {
      if (receipt.provider !== OLO_PROVIDER_KEY || !receipt.lastReceivedAt) {
        return latest
      }
      const receivedAtMs = new Date(receipt.lastReceivedAt).getTime()
      if (!Number.isFinite(receivedAtMs)) return latest
      if (!latest || receivedAtMs > latest.ms) {
        return { iso: receipt.lastReceivedAt, ms: receivedAtMs }
      }
      return latest
    }, null)?.iso ?? null
  )
}
